import { get } from 'lodash'
import PropTypes from 'prop-types'
import { Redirect, Route, useLocation } from 'react-router-dom'

import Loading from './Loading'
import { useRoles } from './RolesContainer'

const AuthenticatedRoute = ({
  component: Component,
  currentSpaceId,
  exact,
  isAuthenticated,
  isContextLoading,
  organizationId,
  path,
  requiresAuthentication,
  requiresRoles
}: any) => {
  const roles = useRoles()
  const location = useLocation()

  const isHomePage = (pathname: any) => pathname === '/'
  const hasAccess = () => {
    return (
      !requiresAuthentication ||
      (isAuthenticated && roles.isAllowedAccess(requiresRoles, organizationId, currentSpaceId))
    )
  }

  //test for space in the url and see if it matches what we have for the currentSpaceId, redirect to homepage for anything not in the correct space
  const match = location.pathname.match(/^\/space\/(?<spaceId>[\dA-Za-z-]*?)(\/.*)?$/)
  const urlSpaceId = get(match, 'groups.spaceId', null)
  const redirectUrl = urlSpaceId ? (urlSpaceId === currentSpaceId ? `/space/${currentSpaceId}` : '/') : '/'

  return (
    <Route
      exact={exact}
      path={path}
      render={props => {
        if (requiresAuthentication && isContextLoading && !isHomePage(props.location.pathname)) {
          /*
           * Do NOT render the component until the console and tenant configurations, user and organization context, and
           * roles have been loaded. This allows us to trust that current organization, space and user context data is
           * available in all downstream authenticated components without needing null checks.
           *
           * Note: The homepage has it's own skeleton loader so we don't block rendering of that page component.
           */
          return <Loading />
        } else if (hasAccess() && (!urlSpaceId || urlSpaceId === currentSpaceId)) {
          // only load the component if we are in a space page and the url matches what we have in our state for current space
          // provide the active organization and space identifiers to EVERY component so they don't have to be selected
          return <Component {...props} currentSpaceId={currentSpaceId} organizationId={organizationId} />
        }

        return (
          <Redirect
            to={{
              pathname: requiresAuthentication && !isAuthenticated ? '/login' : redirectUrl,
              state: { from: props.location }
            }}
          />
        )
      }}
    />
  )
}

AuthenticatedRoute.displayName = 'AuthenticatedRoute'

AuthenticatedRoute.propTypes = {
  component: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.object]).isRequired,
  currentSpaceId: PropTypes.string,
  exact: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  isContextLoading: PropTypes.bool,
  organizationId: PropTypes.string,
  path: PropTypes.string.isRequired,
  requiresAuthentication: PropTypes.bool,
  requiresRoles: PropTypes.arrayOf(PropTypes.string)
}

AuthenticatedRoute.defaultProps = {
  currentSpaceId: null,
  exact: true,
  isAuthenticated: false,
  isContextLoading: false,
  organizationId: null,
  requiresAuthentication: true,
  requiresRoles: null
}

export default AuthenticatedRoute
